import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const DEFAULT_CLOSE_THRESHOLD = 96

const useSheetDragDismiss = ({
  enabled = true,
  closeThreshold = DEFAULT_CLOSE_THRESHOLD,
  onDismiss
} = {}) => {
  const pointerStateRef = useRef({
    active: false,
    pointerId: null,
    startY: 0
  })
  const dragOffsetRef = useRef(0)
  const [dragOffset, setDragOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)

  const resetDrag = useCallback(() => {
    pointerStateRef.current = {
      active: false,
      pointerId: null,
      startY: 0
    }
    dragOffsetRef.current = 0
    setIsDragging(false)
    setDragOffset(0)
  }, [])

  useEffect(() => {
    if (!enabled) {
      resetDrag()
    }
  }, [enabled, resetDrag])

  const handlePointerDown = useCallback((event) => {
    if (!enabled) return
    if (event.button !== undefined && event.button !== 0) return

    pointerStateRef.current = {
      active: true,
      pointerId: event.pointerId ?? null,
      startY: event.clientY ?? 0
    }
    dragOffsetRef.current = 0
    setDragOffset(0)
    setIsDragging(true)

    if (typeof event.currentTarget?.setPointerCapture === 'function' && event.pointerId !== undefined) {
      event.currentTarget.setPointerCapture(event.pointerId)
    }
  }, [enabled])

  const handlePointerMove = useCallback((event) => {
    const pointerState = pointerStateRef.current
    if (!enabled || !pointerState.active) return
    if (pointerState.pointerId !== null && event.pointerId !== pointerState.pointerId) return

    const nextOffset = Math.max(0, (event.clientY ?? 0) - pointerState.startY)
    dragOffsetRef.current = nextOffset
    setDragOffset(nextOffset)
  }, [enabled])

  const finishDrag = useCallback((event, shouldDismiss) => {
    const pointerState = pointerStateRef.current
    if (!pointerState.active) return
    if (pointerState.pointerId !== null && event.pointerId !== pointerState.pointerId) return

    if (
      typeof event.currentTarget?.releasePointerCapture === 'function'
      && pointerState.pointerId !== null
      && event.currentTarget.hasPointerCapture?.(pointerState.pointerId)
    ) {
      event.currentTarget.releasePointerCapture(pointerState.pointerId)
    }

    resetDrag()
    if (shouldDismiss && typeof onDismiss === 'function') {
      onDismiss()
    }
  }, [onDismiss, resetDrag])

  const handlePointerUp = useCallback((event) => {
    const shouldDismiss = dragOffsetRef.current >= closeThreshold
    finishDrag(event, shouldDismiss)
  }, [closeThreshold, finishDrag])

  const handlePointerCancel = useCallback((event) => {
    finishDrag(event, false)
  }, [finishDrag])

  const dragHandleProps = useMemo(() => ({
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerCancel
  }), [handlePointerCancel, handlePointerDown, handlePointerMove, handlePointerUp])

  return {
    dragHandleProps,
    dragOffset,
    isDragging,
    resetDrag
  }
}

export default useSheetDragDismiss
